/**
 * KAN-562 - Pure decision logic for the course-player profile gate.
 *
 * Harold Buck asked for it directly: an invited learner creates a login, is
 * dropped straight into the course, and never fills in the profile
 * information the organization asked for. The gate holds them on the profile
 * page until those fields are filled.
 *
 * Three properties keep this safe:
 *
 * 1. Self-limiting by construction. It keys off the KAN-468 completion object
 *    from GET /custom-fields/me, which counts only fields the org flagged
 *    `is_requested`. An org that requests nothing reports
 *    requestedCount: 0 / isComplete: true and is untouched - no new org
 *    setting, the existing configuration IS the opt-in.
 * 2. Learners only. Instructors and admins reach the player to preview and to
 *    take courses themselves, and must never be held up.
 * 3. Fail OPEN. This is a nudge, not a security boundary. Anything unknown -
 *    a missing user, an unknown role, an absent completion object - resolves
 *    to "let them through". Server-side enforcement is deliberately absent.
 *
 * Components and guards orchestrate; these functions decide
 * (docs/patterns/angular-fp-patterns.md).
 */

/** The slice of the signed-in user this decision reads. */
export interface ProfileGateUser {
  /**
   * Role in the ACTIVE organization, from user_organizations.role. Never
   * `users.role` - that column is stamped at signup and goes stale (a
   * multi-org account is a learner in one org and an instructor in another).
   */
  currentOrgRole?: string | null;
  isSystemAdmin?: boolean;
}

/** The KAN-468 completion object from GET /custom-fields/me. */
export interface ProfileGateCompletion {
  requestedCount?: number;
  completedCount?: number;
  isComplete?: boolean;
}

/** Only the course player is ever a legitimate return target for this gate. */
const COURSE_PLAYER_PATH = /^\/courses\/[^/?#\\]+/;

/**
 * Whether this person is subject to the gate at all. Learners in the active
 * org, and nobody else; anything unknown fails open.
 */
export const isProfileGatedRole = (user: ProfileGateUser | null | undefined): boolean => {
  if (!user || user.isSystemAdmin === true) {
    return false;
  }
  return (user.currentOrgRole ?? '').toLowerCase() === 'learner';
};

/**
 * Whether to hold this learner on the profile page instead of opening the
 * course. Only an explicit `isComplete === false` gates; every other shape -
 * a missing completion object, a malformed payload - lets them through.
 */
export const shouldGateCoursePlayer = (
  user: ProfileGateUser | null | undefined,
  completion: ProfileGateCompletion | null | undefined
): boolean => isProfileGatedRole(user) && completion?.isComplete === false;

/**
 * Narrow a `returnTo` value down to an in-app course player path, or null.
 * The value round-trips through the URL bar, so treat it as untrusted.
 *
 * COURSE_PLAYER_PATH alone does the whole job, and deliberately so: requiring
 * a literal `/courses/` followed by a segment character that is none of
 * `/ ? # \` rejects absolute URLs, protocol-relative `//host` and
 * backslash-escaped `/\host` on its own. An earlier version added explicit
 * startsWith checks for those two; a mutation run showed they could be deleted
 * with every test still green, i.e. they were dead code sitting in front of the
 * check that actually decides. Widen the character class and those cases come
 * straight back, which is what the specs for them now pin.
 */
export const sanitizeCourseReturnUrl = (raw: string | null | undefined): string | null => {
  const candidate = (raw ?? '').trim();
  return COURSE_PLAYER_PATH.test(candidate) ? candidate : null;
};
